import React, {useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
    AddButton,
    DownloadCsv,
    EditWithIcon,
    FilterPopover,
    FullTable,
    SelectedFilters,
    SelectTypeAutocomplete
} from 'components';
import {CreateStaff, StaffSummaryModal} from 'fragments';
import {adminActions, systemActions, tagActions} from 'store';
import {
    Images,
    PermissionList,
    RolePermission,
    SaveParams,
    sortListByKey,
    staffStatuses,
    tagEnums,
    useModal
} from 'utils';
import {ACTION_TYPE, staffBody, staffDefaultStatuses, staffHead, staffNotYet} from './constants';

export const Staff = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const adminsList = useSelector((state) => state.admins.adminsList);
    const globalDepartments = useSelector((state) => state.system.departments);
    const globalJobs = useSelector((state) => state.system.jobs);
    const tagList = useSelector((state) => state.tags.tagList);
    const {open} = useModal();
    const popoverFiltersCount = info?.titleIds?.length ? 1 : 0;

    const handlePopoverChange = (e) => {
        const params = {...info, skip: 0, page: 1};
        if (!e.target.value || e.target.value === 'All') {
            delete params[e.target.name];
        } else {
            // The endpoint takes an array; the control is single-select, so wrap it.
            params[e.target.name] = [e.target.value];
        }
        SaveParams(navigate, params);
    };

    const resetPopoverFilters = (handleClose) => {
        const params = {...info, skip: 0, page: 1};
        delete params.titleIds;
        SaveParams(navigate, params);
        handleClose?.();
    };

    const renderParams = () => {
        const filteredInfo = {
            ...info,
        };
        filteredInfo.limit = 50;
        filteredInfo.skip = info?.skip ? info?.skip : 0;

        if (info?.department) {
            if (info?.department === '--') {
                filteredInfo.noDepartment = true;
                delete filteredInfo.department;
                delete filteredInfo.departmentIds;
            } else {
                filteredInfo.departmentIds = info?.department;
                delete filteredInfo.department;
                delete filteredInfo.noDepartment;
            }
        } else {
            delete filteredInfo.department;
            delete filteredInfo.noDepartment;
        }

        if (!info?.statuses) {
            filteredInfo.statuses = staffDefaultStatuses;
        } else if (info?.statuses?.length) {
            filteredInfo.statuses = info?.statuses;
        } else {
            delete filteredInfo.statuses;
        }
        delete filteredInfo.page;
        delete filteredInfo.activeTab;
        delete filteredInfo.name;
        return filteredInfo;
    };

    const getStaffs = (load) => {
        dispatch(adminActions.getAdmins(renderParams(), load));
    };

    useEffect(() => {
        getStaffs();
    }, [info]);

    useEffect(() => {
        dispatch(systemActions.getDepartments());
        // Job Title options for the filter popover, and the names the Job Title chips read back.
        dispatch(systemActions.getJobs());
        dispatch(tagActions.getTags({module: tagEnums.STAFF}));
    }, [dispatch]);

    const handleNavigate = (id) => {
        if (RolePermission([PermissionList.STAFF_READ.code])) {
            navigate(`/staff/${id}`);
        }
    };

    return (
        <>
            <div className="space-between" style={{marginBottom: 12}}>
                <div className="flex-align-center" style={{gap: 16}}>
                    <FilterPopover activeFiltersCount={popoverFiltersCount} onResetAll={resetPopoverFilters}>
                        <SelectTypeAutocomplete
                            placeholder={info?.titleIds?.length ? '' : 'All'}
                            name={'titleIds'}
                            handleSelect={handlePopoverChange}
                            defaultValue={info?.titleIds?.[0]}
                            list={globalJobs?.length ? [{id: 'All', name: 'All'}, ...globalJobs] : [{
                                id: 'All',
                                name: 'All'
                            }]}
                            renderValue={(i) => i?.name}
                            uiType={'tableFilter'}
                            outLabel={'Job Title'}
                            noError={true}
                        />
                    </FilterPopover>
                    <EditWithIcon
                        styles={{width: 'fit-content'}}
                        innerText={'Supervision Summary'}
                        id={'supervision-summary'}
                        icon={Images.supervizeBigEye}
                        type={'button'}
                        handleClick={() => {
                            open(<StaffSummaryModal/>);
                        }}
                    />
                </div>

                <div className="flex-align-center" style={{gap: 16}}>
                    <DownloadCsv type={'staff'}/>
                    {RolePermission([PermissionList.STAFF_CREATE.code]) && (
                        <AddButton
                            text={'Add Staff Member'}
                            handleClick={() => open(<CreateStaff afterSuccess={getStaffs}/>)}
                        />
                    )}
                </div>
            </div>
            <SelectedFilters
                doNotCrop
                filtersList={[
                    {name: 'Job Title', key: 'titleIds', options: globalJobs, loading: !globalJobs?.length},
                    {
                        name: 'Department',
                        key: 'department',
                        companions: ['departmentIds', 'noDepartment'],
                        // '--' is the column's own "No Department" entry; everything else resolves
                        // against the global catalog, so it shares the catalog's loading state.
                        options: [{id: '--', name: 'None'}, ...(globalDepartments || [])],
                        loading: !globalDepartments?.length,
                    },
                    {name: 'Status', key: 'statuses', options: staffStatuses, defaultValue: staffDefaultStatuses},
                    {name: 'Tag', key: 'tags', options: tagList, loading: !tagList?.length},
                    {name: 'Search', key: 'searchValue', companions: ['searchKey']},
                ]}
            />

            <FullTable
                head={staffHead(globalDepartments)}
                body={staffBody(renderParams())}
                loadingType={ACTION_TYPE}
                list={sortListByKey(adminsList?.staffs, 'fullName', info)}
                listCount={adminsList?.count}
                handleClick={handleNavigate}
                notYet={staffNotYet}
                detailPage={(item) => RolePermission([PermissionList.STAFF_READ.code]) && `/staff/${item?.id}`}
                margin={'12px 0 0'}
            />
        </>
    );
};
